import React, { memo, useCallback, useEffect, useRef } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { format, isAfter, isBefore, parse } from 'date-fns';
import { ExecutionResult } from 'graphql';
import { IntlFormatters, useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { IStore } from '@rbi-ctg/store';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { useGetRestaurantsQuery } from 'generated/graphql-gateway';
import { ICommitOrderInput, useCommitOrder } from 'hooks/commit-order';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useCart } from 'hooks/use-cart';
import useErrorModal from 'hooks/use-error-modal';
import { useIOSLiveActivities } from 'hooks/use-ios-live-activities';
import { ICommitOrderMutationResponse } from 'remote/queries/order';
import { IValidDayPart } from 'state/day-part/hooks/use-active-day-parts';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DAY_PART_SELECTIONS } from 'state/menu/main-menu/types';
import { OrderStatus, useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import {
  CASH_ACCOUNT_IDENTIFIER,
  PAYPAL_ACCOUNT_IDENTIFIER,
  VENMO_ACCOUNT_IDENTIFIER,
} from 'state/payment/constants';
import { IPaymentDetails } from 'state/payment/types';
import { ServiceMode } from 'state/service-mode';
import {
  GraphQLErrorCodes,
  getErrorDialogForGraphQLCode,
  parseGraphQLErrorCodes,
} from 'utils/errors';
import { IFraudPreventionValues, splitExpiry } from 'utils/payment';
import { readableCloseHourToday } from 'utils/restaurant';
import { DAYPART_FORMAT, TWELVE_HOUR_TIME_PARSE_FORMAT } from 'utils/restaurant/constants';
import { routes } from 'utils/routing';

import { CardButton } from './card-button';

type onCommitType = (
  input: ICommitOrderInput
) => Promise<ExecutionResult<ICommitOrderMutationResponse>>;

type onCommitErrorType = (error: any) => void;

const Pickup = Text.withConfig({
  color: Styles.color.accent,
});

const handleCommitOrder = (
  onCommit: onCommitType,
  onCommitError: onCommitErrorType,
  payment: {
    clearPaymentDetails(): void;
    paymentDetails: IPaymentDetails;
  },
  serviceMode: ServiceMode,
  skipCoolingPeriod?: boolean
) => {
  const { paymentDetails, clearPaymentDetails } = payment;
  const {
    accountIdentifier,
    creditType,
    fdAccessToken,
    fdNonce,
    fraudPreventionValues = {} as IFraudPreventionValues,
    applePayDetails,
    googlePayDetails,
    paypalDetails,
    venmoDetails,
  } = paymentDetails;

  let commitInput: ICommitOrderInput;

  if (accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: CASH_ACCOUNT_IDENTIFIER,
      serviceMode,
      skipCoolingPeriod,
      payment: {
        cashPayment: true,
        fullName: fraudPreventionValues.fullName,
      },
    };
  } else if (accountIdentifier === PAYPAL_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: PAYPAL_ACCOUNT_IDENTIFIER,
      serviceMode,
      skipCoolingPeriod,
      paypalDetails,
    };
  } else if (accountIdentifier === VENMO_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: VENMO_ACCOUNT_IDENTIFIER,
      serviceMode,
      skipCoolingPeriod,
      venmoDetails,
    };
  } else {
    const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

    commitInput = {
      applePayDetails,
      creditType,
      fdAccountId: accountIdentifier,
      fullName: fraudPreventionValues.fullName,
      serviceMode,
      payment: {
        billingAddress: fraudPreventionValues.billingAddress,
        ccMetadata: {
          ccBin: fraudPreventionValues.ccBin,
          ccExpiryMonth: expiryMonth,
          ccExpiryYear: expiryYear,
          ccLast4: fraudPreventionValues.ccLast4,
        },
        firstDataInput: {
          accountIdentifier,
          fdAccessToken,
          fdNonce,
        },
        fullName: fraudPreventionValues.fullName,
      },
      googlePayDetails,
      skipCoolingPeriod,
    };
  }

  return onCommit(commitInput)
    .then(() => {
      clearPaymentDetails();
    })
    .catch(onCommitError);
};

interface IGetHoursText {
  dayParts: readonly IValidDayPart[];
  formatMessage: IntlFormatters['formatMessage'];
  hours: Pick<IStore, 'driveThruHours' | 'diningRoomHours'>;
  orderedAt: Date;
  serviceMode: ServiceMode;
}

// TODO: RN - locale switching -- can we get rid of this? only used in test files?
// if not, will need to update to accept timeFormatConfig prop to pass to readableCloseHourToday
// e.x const timeFormatConfig = useConfigValue({ key: 'timeFormat' });
export const getHoursText = ({
  dayParts,
  formatMessage,
  hours,
  orderedAt,
  serviceMode,
}: IGetHoursText) => {
  const { startTime, endTime } =
    dayParts.find(dayPart => dayPart.key === DAY_PART_SELECTIONS.BREAKFAST_KEY) || {};
  const breakfastStartTime = startTime ? parse(startTime, DAYPART_FORMAT, new Date()) : null;
  const breakfastEndTime = endTime ? parse(endTime, DAYPART_FORMAT, new Date()) : null;

  const isBreakfastOrder =
    breakfastStartTime && breakfastEndTime
      ? isAfter(orderedAt, breakfastStartTime) && isBefore(orderedAt, breakfastEndTime)
      : false;

  const serviceModeHours =
    serviceMode === ServiceMode.DRIVE_THRU ? hours.driveThruHours : hours.diningRoomHours;

  const pickupTime =
    isBreakfastOrder && breakfastEndTime
      ? format(breakfastEndTime, TWELVE_HOUR_TIME_PARSE_FORMAT)
      : readableCloseHourToday(serviceModeHours);

  // TODO not sure why this is having issues with typescript update, casting to any to fix for now.
  return formatMessage({ id: 'pickUpBefore' }, {
    pickupTime,
    pickup: (str: string) => <Pickup>{str}</Pickup>,
  } as any);
};

const PickupCard = ({
  serverOrder,
  needConfirm,
  setNeedConfirmation,
  setProcessingPayment,
}: {
  confirmed?: boolean;
  serverOrder: IServerOrder;
  needConfirm?: boolean;
  setNeedConfirmation?: (value: boolean) => void;
  setProcessingPayment?: (value: boolean) => void;
}) => {
  const { formatMessage } = useIntl();
  const { onCommitSuccess } = useOrderContext();
  const { emptyCart } = useCart();
  const { navigate } = useNavigation();

  const { serviceMode } = serverOrder.cart;

  const { commitOrder, loading, orderStatus, commitResult } = useCommitOrder({
    rbiOrderId: serverOrder.rbiOrderId,
  });

  const { data: restaurantsData, loading: loadingRestaurant } = useGetRestaurantsQuery({
    variables: {
      input: {
        first: 1,
        coordinates: {
          userLat: serverOrder.cart.storeDetails.latitude,
          userLng: serverOrder.cart.storeDetails.longitude,
          searchRadius: 20,
        },
      },
    },
  });

  const {
    clearPaymentDetails,
    paymentDetails,
    loading: paymentDetailsLoading,
  } = usePaymentContext();
  const onCommitError = (err: any) => {
    throw err;
  };

  const onCommitOrderSkipCooldown = useCallback(() => {
    const payment = {
      clearPaymentDetails,
      paymentDetails,
    };

    handleCommitOrder(commitOrder, onCommitError, payment, serviceMode, true).then(() => {
      onCommitSuccess(serverOrder);
      return emptyCart();
    });
  }, [
    clearPaymentDetails,
    commitOrder,
    emptyCart,
    onCommitSuccess,
    paymentDetails,
    serverOrder,
    serviceMode,
  ]);

  const { updateActivity } = useIOSLiveActivities();

  const onCommitOrder = useCallback(async () => {
    const payment = {
      clearPaymentDetails,
      paymentDetails,
    };

    await handleCommitOrder(commitOrder, onCommitError, payment, serviceMode, undefined);
    onCommitSuccess(serverOrder);
    updateActivity(serverOrder, false);
    return emptyCart();
  }, [
    clearPaymentDetails,
    commitOrder,
    emptyCart,
    onCommitSuccess,
    paymentDetails,
    serverOrder,
    serviceMode,
    updateActivity,
  ]);

  const onCancelModal = () => navigate(routes.orders);
  const [OrderCooldownErrorDialog, openOrderCooldownErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
  });

  const orderIsLoading =
    loading || paymentDetailsLoading || orderStatus === OrderStatus.INSERT_REQUESTED;

  const enableCheckoutAnimation = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_ANIMATION);
  const isOrderStatusFailureHandledRef = useRef(false);

  useEffect(() => {
    if (!commitResult.error) {
      return;
    }

    const [parsedGqlError] = parseGraphQLErrorCodes(commitResult.error);
    if (!parsedGqlError) {
      return;
    }

    const mappedErrorCode = getErrorDialogForGraphQLCode(parsedGqlError, formatMessage);
    const isCommitOrderCoolingPeriodError =
      parsedGqlError.errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD;

    if (
      mappedErrorCode &&
      !isOrderStatusFailureHandledRef.current &&
      isCommitOrderCoolingPeriodError
    ) {
      isOrderStatusFailureHandledRef.current = true;
      openOrderCooldownErrorDialog({
        body: formatMessage({ id: 'errorDialogCooldownMessage' }),
        title: formatMessage({ id: 'errorDialogCooldownTitle' }),
        message: formatMessage({ id: 'errorDialogCooldownMessage' }),
        dismissText: formatMessage({ id: 'viewRecentOrders' }),
        confirmationText: formatMessage({ id: 'placeOrder' }),
        onDismiss: () => onCancelModal(),
        onConfirm: () => onCommitOrderSkipCooldown(),
      });
    }
  }, [commitResult, formatMessage, openOrderCooldownErrorDialog]);

  return (
    <>
      <Box testID="pickup-card">
        <VisuallyHidden
          role="alert"
          accessibilityLabel={formatMessage(
            { id: 'pickUpConfirmationScreenReaderInstructions' },
            {
              screenReaderButtonName: 'imHere',
            }
          )}
        />
      </Box>
      {!loadingRestaurant && !!restaurantsData?.restaurants?.nodes[0] && (
        <>
          {needConfirm && (
            <CardButton
              isLoading={orderIsLoading}
              onCommit={() => {
                if (enableCheckoutAnimation && setProcessingPayment) {
                  setProcessingPayment(true);
                }
                if (enableCheckoutAnimation && setNeedConfirmation) {
                  setNeedConfirmation(false);
                }
                onCommitOrder();
              }}
              orderStatus={orderStatus}
              restaurant={restaurantsData?.restaurants?.nodes[0]}
            />
          )}
        </>
      )}

      <OrderCooldownErrorDialog />
    </>
  );
};

export default memo(PickupCard);
